import { v4 as uuid } from "uuid"
import { QuickTradeProto, QuickTradeType, QuoteType } from "../proto/generated/quick-trade"
import { Broker_OrderType, TransactionType } from "../proto/generated/order"
import { OrderDataPrices, OrdersDataType } from "../reducer/orders.reducer"
import { getOrderBooksBulk, orderTypeToTransactionType, roundToNearestMultiple } from "./tradepanel"
import { limitPriceFromOrderBookOrLtp } from "./orderBookPrice"
import { getSecurityAsync, getSecurityKey } from "./security"
import { Security } from "../proto/generated/security"

export const defaultQuickTrade = (): QuickTradeProto => {
    return {
        id: uuid(),
        actionName: '',
        type: QuickTradeType.TEMPLATE_ORDER,
        referenceId: '',
        // security: {
        //     code: security?.code ?? 'NIFTY_50',
        //     exchange: security?.exchange ?? Exchange.NSE,
        // },
        templateOrderExecutionParameters: {
            brokerOrderType: Broker_OrderType.MARKET_ORDER,
            multiplier: 1,
            amount: undefined
        },
        description: '',
    }
}

export const getOrderPricesFromQuote = async (orders: OrdersDataType[], orderPrices: OrderDataPrices[], quoteType: QuoteType | undefined, quoteValue: number | undefined): Promise<OrderDataPrices[]> => {
    if(!quoteType || quoteValue === undefined) {
        return orderPrices
    }
    const newOrderPrices: OrderDataPrices[] = [...orderPrices]
    let bufferPrice: number = 0
    const sec = await getSecurityAsync(getSecurityKey(orders[0].security?.exchange??0, orders[0].security?.code??''))
    let level = 1
    const securitiesToFetchOrderBook = orders.map(o=>(({
        code: o.security?.code,
        exchange: o.security?.exchange
    }) as Security))
    const orderBooksData = await getOrderBooksBulk(securitiesToFetchOrderBook)
    if(quoteType===QuoteType.BIPS) {
        bufferPrice = quoteValue * 0.0001
        bufferPrice = Number(roundToNearestMultiple(quoteValue/(sec.priceMultiplier??100), TransactionType.BUY, true, (sec.tickSize??5)/(sec.priceMultiplier??100)).toFixed(2))
    } else if(quoteType===QuoteType.TICKS) {
        bufferPrice = (quoteValue * (sec.tickSize ?? 5)) / (sec.priceMultiplier ?? 100)
    } else if(quoteType===QuoteType.LEVEL) {
        level = quoteValue
        if(level > 5) {
            level = 5
        } else if(level < 1) {
            level = 1
        }
    }
    orders.forEach((order, i) => {
        if(quoteValue===0) return
        if(quoteType === QuoteType.BIPS || quoteType === QuoteType.TICKS) {
            const newBufferPrice = quoteType===QuoteType.BIPS ? roundToNearestMultiple(Number(orderPrices[i].price) * bufferPrice, orderTypeToTransactionType(order.type), false, sec.tickSize??5) : bufferPrice
            if(order.type === 'buy') {
                newOrderPrices[i].price = (Number(orderPrices[i].price) - newBufferPrice).toFixed(2)
            } else {
                newOrderPrices[i].price = (Number(orderPrices[i].price) + newBufferPrice).toFixed(2)
            }
        } else if(quoteType === QuoteType.LEVEL) {
            const orderBook = orderBooksData.find(ob => ob.security?.code === order.security?.code && ob.security?.exchange === order.security?.exchange)
            if(orderBook) {
                if(order.type === 'buy') {
                    newOrderPrices[i].price = (orderBook?.[quoteValue>0 ? 'bids' : 'asks']?.[level]?.price ?? Number(orderPrices[i].price)).toFixed(2)
                } else {
                    newOrderPrices[i].price = (orderBook?.[quoteValue>0 ? 'asks' : 'bids']?.[level]?.price ?? Number(orderPrices[i].price)).toFixed(2)
                }
            }
        }
    })
    orders.forEach((order, i) => {
        newOrderPrices[i].price = (roundToNearestMultiple(Number(newOrderPrices[i].price)*(sec.priceMultiplier??100), orderTypeToTransactionType(order.type), false, sec.tickSize??5)/(sec.priceMultiplier??100)).toFixed(2)
    })
    return newOrderPrices
}

export const getOrderPriceFromQuote = async (security: Security, transactionType: TransactionType, quoteType: QuoteType | undefined, quoteValue: number | undefined): Promise<string> => {
    let resultPrice = '0'
    let bufferPrice: number = 0
    let level = 1
    const orderBooksData = await getOrderBooksBulk([security])
    const ob = orderBooksData[0]
    const askP = ob?.asks?.[0]?.price ?? 0
    const bidP = ob?.bids?.[0]?.price ?? 0
    let midRaw = askP > 0 && bidP > 0 ? (askP + bidP) / 2 : 0
    if (midRaw <= 0) {
        midRaw = limitPriceFromOrderBookOrLtp(ob, transactionType, undefined)
    }
    const price = (roundToNearestMultiple(midRaw, transactionType, false, security.tickSize))/(security.priceMultiplier??100)
    if(quoteValue===0) return price.toFixed(2)
    if(quoteType===QuoteType.BIPS) {
        bufferPrice = price * quoteValue * 0.0001
    } else if(quoteType===QuoteType.TICKS) {
        bufferPrice = (quoteValue * (security.tickSize ?? 5)) / (security.priceMultiplier ?? 100)
    } else if(quoteType===QuoteType.LEVEL) {
        level = quoteValue
        if(level > 5) {
            level = 5
        } else if(level < 1) {
            level = 1
        }
    }
    if(quoteType === QuoteType.BIPS || quoteType === QuoteType.TICKS) {
        if(transactionType === TransactionType.BUY) {
            resultPrice = (Number(price) - bufferPrice).toFixed(2)
        } else {
            resultPrice = (Number(price) + bufferPrice).toFixed(2)
        }
    } else if(quoteType === QuoteType.LEVEL) {
        const orderBook = orderBooksData.find(ob => ob.security?.code === security?.code && ob.security?.exchange === security?.exchange)
        if(orderBook) {
            if(transactionType === TransactionType.BUY) {
                resultPrice = (orderBook?.[quoteValue>0 ? 'bids' : 'asks']?.[level]?.price ?? Number(price)).toFixed(2)
            } else {
                resultPrice = (orderBook?.[quoteValue>0 ? 'asks' : 'bids']?.[level]?.price ?? Number(price)).toFixed(2)
            }
        }
    }
    resultPrice = (roundToNearestMultiple(Number(resultPrice)*(security.priceMultiplier??100), TransactionType.BUY, false, security.tickSize)/(security.priceMultiplier??100)).toFixed(2)
    return resultPrice
}